/**
 * Locations Service Implementation
 * Handles fetching nearby pickup locations from the API
 */

import type { AxiosInstance } from "axios";
import type { LocationsService } from "../contracts/locations.contract";
import type { RequestConfig } from "../types/common";
import type {
	GetNearbyLocationsRequest,
	GetNearbyLocationsResponse,
	PickupLocation,
	PickupLocationRaw,
	TransformedLocationsResponse,
} from "../types/moneygram";

export class MoneyGramLocationsService implements LocationsService {
	constructor(private readonly httpClient: AxiosInstance) {}

	async getNearbyLocations(
		request: GetNearbyLocationsRequest,
		config?: RequestConfig,
	): Promise<TransformedLocationsResponse> {
		try {
			// Validate country code format - API expects ISO2 format (2 characters)
			const isValidISO2 = /^[A-Z]{2}$/.test(request.country);

			if (!isValidISO2) {
				console.warn(
					"⚠️ Locations API Warning: Country code should be ISO2 format (2 characters), received:",
					{
						country: request.country,
						length: request.country?.length,
						format: request.country?.length === 3 ? "ISO3" : "unknown",
					},
				);
			}

			const payload = {
				latitude: request.latitude,
				longitude: request.longitude,
				searchRadius: request.searchRadius,
				limit: request.limit,
				country: request.country,
				radiusuom: request.radiusuom,
				agent: request.agent,
			};

			const response = await this.httpClient.post(
				"/config-data/locations/fetch",
				payload,
				{
					signal: config?.signal,
					timeout: config?.timeout,
				},
			);

			return this.transformLocationsResponse(response.data);
		} catch (error) {
			console.error("❌ Locations API Error:", error);
			throw this.handleApiError(
				error,
				`Failed to fetch nearby locations for coordinates ${request.latitude}, ${request.longitude}`,
			);
		}
	}

	async getLocationById(
		locationId: string,
		config?: RequestConfig,
	): Promise<PickupLocation | null> {
		try {
			const response = await this.httpClient.get(`/locations/${locationId}`, {
				signal: config?.signal,
				timeout: config?.timeout,
			});

			return this.transformLocationResponse(response.data);
		} catch (error) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as { response?: { status?: number } };
				if (axiosError.response?.status === 404) {
					return null;
				}
			}
			throw this.handleApiError(
				error,
				`Failed to fetch location details for ID ${locationId}`,
			);
		}
	}

	/**
	 * Transform API response to our internal format
	 */
	private transformLocationsResponse(
		data: GetNearbyLocationsResponse,
	): TransformedLocationsResponse {
		return {
			locations: data.locations?.map(this.transformLocationResponse) || [],
			radius: data.radius || 10,
		};
	}

	/**
	 * Transform individual location data from raw API format to UI format
	 */
	private transformLocationResponse(data: PickupLocationRaw): PickupLocation {
		const {
			locationDetails,
			locationOperatingHours,
			locationPhone,
			locationServices,
			locationAnalytics,
		} = data;

		return {
			id: locationDetails.locationId,
			name: locationDetails.name,
			address: [
				locationDetails.addressLine1,
				locationDetails.addressLine2,
				locationDetails.addressLine3,
			]
				.filter(Boolean)
				.join(", "),
			city: locationDetails.city,
			state: locationDetails.state,
			zipCode: locationDetails.postalCode,
			country: locationDetails.country,
			latitude: locationDetails.latitude,
			longitude: locationDetails.longitude,
			distance: locationDetails.distance,
			distanceUnit: locationDetails.distanceuom,
			phone: locationPhone.phone,
			hours: locationOperatingHours,
			services: locationServices,
			analytics: locationAnalytics,
		};
	}

	/**
	 * Handle API errors consistently
	 */
	private handleApiError(error: unknown, context: string): Error {
		let message = "Unknown error";
		let status: number | undefined;

		if (error && typeof error === "object") {
			if (
				"response" in error &&
				error.response &&
				typeof error.response === "object"
			) {
				const response = error.response as Record<string, unknown>;
				status = response.status as number;
				if (
					"data" in response &&
					response.data &&
					typeof response.data === "object"
				) {
					const data = response.data as Record<string, unknown>;
					message = (data.message as string) || message;
				}
			}
			if ("message" in error && typeof error.message === "string") {
				message = error.message;
			}
		}

		console.error(`API Error [${status}]: ${context}`, {
			error: message,
			status,
			context,
		});

		return new Error(`${context}: ${message}`);
	}
}
